import type { ReactNode, CSSProperties } from "react";
import { useFeatureFlags } from "@/lib/FeatureFlagContext";

const IS_DEV = import.meta.env.DEV;

interface FeatureFlagZoneProps {
  flagKey: string;
  label?: string;
  children: ReactNode;
}

function ZoneLabel({
  flagKey,
  label,
  effectiveValue,
  source,
  prodDefault,
  tested,
  mvpComplete,
}: {
  flagKey: string;
  label?: string;
  effectiveValue: boolean;
  source: "override" | "db";
  prodDefault: boolean | undefined;
  tested: boolean | undefined;
  mvpComplete: boolean | undefined;
}) {
  const color = source === "override"
    ? "#f59e0b"
    : effectiveValue ? "#22c55e" : "#ef4444";

  const labelStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 99999,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 6px",
    fontSize: 10,
    fontFamily: "monospace",
    fontWeight: 700,
    lineHeight: "16px",
    background: "#0d0d0dee",
    color: color,
    border: `1px solid ${color}55`,
    borderRadius: "0 0 4px 0",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
  };

  const pillStyle = (on: boolean): CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 4px",
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 700,
    background: on ? "#22c55e22" : "#ef444422",
    color: on ? "#4ade80" : "#f87171",
    border: `1px solid ${on ? "#22c55e44" : "#ef444444"}`,
  });

  const srcPillStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 4px",
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 700,
    background: source === "override" ? "#f59e0b22" : "#6366f122",
    color: source === "override" ? "#fbbf24" : "#818cf8",
    border: `1px solid ${source === "override" ? "#f59e0b44" : "#6366f144"}`,
  };

  return (
    <span style={labelStyle}>
      <span style={{ color: "#a78bfa" }}>{label ?? flagKey}</span>
      <span style={pillStyle(effectiveValue)}>{effectiveValue ? "ON" : "OFF"}</span>
      <span style={srcPillStyle}>{source === "override" ? "override" : "DB"}</span>
      {prodDefault !== undefined && (
        <span style={{ color: "#555", fontSize: 9 }}>
          prod: {prodDefault ? "✓" : "✗"}
        </span>
      )}
      {tested && <span title="Tested" style={{ color: "#4ade80", fontSize: 9 }}>✓T</span>}
      {mvpComplete && <span title="MVP Complete" style={{ color: "#60a5fa", fontSize: 9 }}>✓M</span>}
    </span>
  );
}

export function FeatureFlagZone({ flagKey, label, children }: FeatureFlagZoneProps) {
  const { isFlagEnabled, devOverrides, flagMeta, showZoneIndicators } = useFeatureFlags();

  if (!IS_DEV || !showZoneIndicators) {
    return <>{children}</>;
  }

  const effectiveValue = isFlagEnabled(flagKey);
  const override = devOverrides[flagKey];
  const source: "override" | "db" = override !== null && override !== undefined ? "override" : "db";

  const meta = flagMeta.find((m) => m.key === flagKey);

  const outlineColor = source === "override"
    ? "#f59e0b"
    : effectiveValue ? "#22c55e55" : "#ef444455";

  const wrapStyle: CSSProperties = {
    position: "relative",
    outline: `2px solid ${outlineColor}`,
    outlineOffset: 2,
  };

  return (
    <div style={wrapStyle} data-ff-zone={flagKey}>
      <ZoneLabel
        flagKey={flagKey}
        label={label}
        effectiveValue={effectiveValue}
        source={source}
        prodDefault={meta?.prodDefault}
        tested={meta?.tested}
        mvpComplete={meta?.mvpComplete}
      />
      {children}
    </div>
  );
}
